/**
 * @module IDEE/layer/XYZ
 */
import XYZImpl from 'impl/layer/XYZ';
import LayerBase from './Layer';
import {
  isUndefined, isObject, isNullOrEmpty, isString,
} from '../util/Utils';
import Exception from '../exception/exception';
import * as parameter from '../parameter/parameter';
import * as LayerType from './Type';
import { getValue } from '../i18n/language';

/**
 * @classdesc
 * Las capas XYZ son servicios de información geográfica en forma de mosaicos.
 * Cada mosaico representa una combinación de tres parámetros.
 * Las capas XYZ tienen la siguiente estructura.
 *
 * https://URL/{z}/{x}/{y}.jpg
 *
 * Donde {z} especifica el nivel de zoom, {x} el número de columna y {y} el número de fila.
 *
 * @property {String} idLayer Identificador de la capa.
 * @property {String} url Url del servicio XYZ.
 * @property {String} name Identificador de capa.
 * @property {String} legend Nombre asociado en el árbol de contenido, si usamos uno.
 * @property {Number} minZoom Limitar el zoom mínimo.
 * @property {Number} maxZoom Limitar el zoom máximo.
 * @property {Number} tileGridMaxZoom Zoom máximo de la tesela en forma de rejilla.
 * @property {Object} options Opciones de capa XYZ.
 * @property {Boolean} isBase Define si la capa es base.
 * @property {Boolean} transparent (deprecated) Falso si es una capa base,
 * verdadero en caso contrario.
 * @property {Array} maxExtent La medida en que restringe la visualización a una región específica.
 * @property {Boolean|String} extract Activa la consulta con GetFeatureInfo; por defecto falso.
 * Puede ser true/false, cadena vacía (equivalente a true), o lista tiles/colors/elevation.
 * En elevation se admite `elevation` (zoom mínimo 15) o `elevation:16`.
 *
 * @api
 * @extends {IDEE.layer}
 */
class XYZ extends LayerBase {
  /**
   * Constructor principal de la clase. Crea una capa XYZ
   * con parámetros especificados por el usuario.
   *
   * @constructor
   * @param {string|Mx.parameters.XYZ} userParameters Parámetros para la construcción de la capa.
   * - attribution: Atribución de la capa.
   * - name: Nombre de la capa.
   * - isBase: Indica si la capa es base.
   * - transparent (deprecated): Falso si es una capa base, verdadero en caso contrario.
   * - maxExtent: La medida en que restringe la visualización a una región específica.
   * - legend: Nombre asociado en el árbol de contenidos, si usamos uno.
   * - visibility: Indica si la capa estará por defecto visible o no.
   * - displayInLayerSwitcher: Indica si la capa se muestra en el selector de capas.
   * - url: URL del servicio XYZ.
   * - type: Tipo de la capa.
   * - tileGridMaxZoom: Zoom máximo de cuadrícula de mosaico.
   * - tileSize: Tamaño de la tesela
   * - extract: Activa GetFeatureInfo. Por defecto falso. true o cadena vacía: teselas y colores.
   *   Cadena con tiles, colors y/o elevation. `elevation` usa zoom mínimo 15;
   *   `elevation:16` (o elevation=16) fija ese zoom mínimo de consulta.
   * @param {Mx.parameters.LayerOptions} options Parámetros opcionales para la capa.
   * - opacity: Opacidad de capa, por defecto 1.
   * - minZoom: Zoom mínimo aplicable a la capa.
   * - maxZoom: Zoom máximo aplicable a la capa.
   * - minScale: Escala mínima.
   * - maxScale: Escala máxima.
   * - crossOrigin: Atributo crossOrigin para las imágenes cargadas.
   * @param {Object} vendorOptions Opciones para la biblioteca base. Ejemplo vendorOptions:
   * <pre><code>
   * import XYZSource from 'ol/source/XYZ';
   * {
   *  opacity: 0.1,
   *  source: new XYZSource({
   *    attributions: 'xyz',
   *    ...
   *  })
   * }
   * </code></pre>
   * @api
   */
  constructor(userParameters, options = {}, vendorOptions = {}) {
    // checks if the implementation can create XYZ
    if (isUndefined(XYZImpl) || (isObject(XYZImpl) && isNullOrEmpty(Object.keys(XYZImpl)))) {
      Exception(getValue('exception').xyz_method);
    }

    if (isString(userParameters) || !isUndefined(userParameters.transparent)) {
      // eslint-disable-next-line no-console
      console.warn(getValue('exception').transparent_deprecated);
    }

    const parameters = parameter.layer(userParameters, LayerType.XYZ);
    /**
     * Implementación de esta capa.
     * @public
     * @implements {IDEE.impl.layer.XYZ}
     * @type {IDEE.impl.layer.XYZ}
     */

    const optionsVar = options;

    if (typeof userParameters !== 'string') {
      optionsVar.maxExtent = userParameters.maxExtent;
    }

    const impl = new XYZImpl(userParameters, optionsVar, vendorOptions);
    // calls the super constructor
    super(parameters, impl);
    this.constructorParameters = { userParameters, options, vendorOptions };

    /**
     * XYZ url: Url del servicio XYZ.
     */
    this.url = parameters.url;

    /**
     * XYZ maxextent: Extensión de visualización
     */
    this.userMaxExtent = userParameters.maxExtent;

    /**
     * XYZ name: Identificador de capa.
     */
    this.name = parameters.name;
    /**
     * XYZ legend: Nombre asociado en el árbol de contenido, si usamos uno.
     */
    this.legend = parameters.legend;

    /**
     * XYZ extract: consulta GetFeatureInfo (teselas, colores y/o elevación).
     * elevation o elevation:N (zoom mínimo de consulta; por defecto 15).
     * @type {boolean|string}
     */
    if (isUndefined(parameters.extract)) {
      this.extract = false;
    } else {
      this.extract = parameters.extract;
    }

    /**
     * XYZ tileGridMaxZoom. Zoom máximo de cuadrícula de mosaico.
     */
    this.tileGridMaxZoom = parameters.tileGridMaxZoom || userParameters.tileGridMaxZoom;

    if (isUndefined(this.tileGridMaxZoom)) {
      /**
       * XYZ minZoom: Límite del zoom mínimo.
       */
      this.minZoom = options.minZoom || Number.NEGATIVE_INFINITY;

      /**
       * XYZ maxZoom: Límite del zoom máximo.
       */
      this.maxZoom = options.maxZoom || Number.POSITIVE_INFINITY;
    }

    /**
     * XYZ options: Opciones de la capa.
     */
    this.options = options;
  }

  /**
   * Este método comprueba si un objeto es igual
   * a esta capa.
   *
   * @function
   * @param {Object} obj Objeto a comparar.
   * @returns {Boolean} Valor verdadero es igual, falso no lo es.
   * @api
   */
  equals(obj) {
    let equals = false;
    if (obj instanceof XYZ) {
      equals = (this.url === obj.url);
      equals = equals && (this.name === obj.name);
      equals = equals && (this.idLayer === obj.idLayer);
    }
    return equals;
  }

  /**
   * Obtiene el índice de tesela (z, x, y) en formato URL XYZ para una coordenada del mapa.
   *
   * @function
   * @public
   * @param {Array<number>} coordinate Coordenadas en la proyección del mapa.
   * @param {number} [zoom] Nivel de zoom de tesela; si se omite, el de la vista.
   * @returns {{z: number, x: number, y: number}|null}
   * Índice de tesela o null si no está disponible.
   * @api
   */
  getTileIndexAtCoordinate(coordinate, zoom) {
    const impl = this.getImpl();
    if (!impl || typeof impl.getTileIndexAtCoordinate !== 'function') {
      return null;
    }
    return impl.getTileIndexAtCoordinate(coordinate, zoom);
  }

  /**
   * Obtiene el color del píxel renderizado en coordenadas de pantalla del mapa.
   * Delega en la implementación (OpenLayers Tile#getData).
   *
   * @function
   * @public
   * @param {Array<number>} pixel Coordenadas de píxel [x, y] del mapa.
   * @returns {Uint8ClampedArray|Uint8Array|Float32Array|DataView|null} Componentes RGBA o null.
   * @api
   */
  getData(pixel) {
    const impl = this.getImpl();
    if (!impl || typeof impl.getData !== 'function') {
      return null;
    }
    return impl.getData(pixel);
  }

  /**
   * Índice de tesela y color de píxel para GetFeatureInfo con elevación.
   * Por debajo del zoom mínimo de consulta carga la tesela a ese nivel.
   *
   * @function
   * @public
   * @param {Array<number>} coordinate Coordenadas del clic.
   * @param {Array<number>} pixel Coordenadas de píxel [x, y] del mapa.
   * @param {number} [minQueryZoom] Zoom mínimo de tesela para la consulta.
   * @returns {Promise<{tileIndex: Object|null, data: Uint8ClampedArray|null}|null>}
   * @api
   */
  getFeatureInfoPixelData(coordinate, pixel, minQueryZoom) {
    const impl = this.getImpl();
    if (!impl || typeof impl.getFeatureInfoPixelData !== 'function') {
      return Promise.resolve({
        tileIndex: this.getTileIndexAtCoordinate(coordinate),
        data: this.getData(pixel),
      });
    }
    return impl.getFeatureInfoPixelData(coordinate, pixel, minQueryZoom);
  }
}

export default XYZ;
